import api from "./api";
import type {
  DisciplineGroundType,
  DisciplineStandard,
  Recommendation,
  DisciplineCaseListResponse,
  DisciplineCaseListItem,
  DisciplineCaseDetail,
  MyDisciplineCaseDetail,
  DisciplineDocument,
  DisciplineComment,
  DisciplineStats,
  DisciplineActionType,
  IntentLevel,
  CommitteeMemberRole,
  VoteType,
} from "@/types/discipline";

export interface CaseListParams {
  status?: string;
  target_user_id?: string;
  action_type?: string;
  severity_class?: string;
  search?: string;
  deleted?: boolean; // 휴지통(소프트삭제) 보기 — 관리자 전용
  page?: number;
  page_size?: number;
}

export interface CaseCreatePayload {
  target_user_id: string;
  title: string;
  ground_type_id?: number;
  incident_description?: string;
  incident_date?: string;
  incident_occurred_date?: string;
  intent_level?: IntentLevel;
}

export interface DisciplineAccess {
  can_register: boolean;   // 등록자 이상(본부장은 등록·조회만, 본인 부서+하위 스코프)
  is_full_admin: boolean;  // 관리자(처분·종결·위원회·기준설정 등 전 기능)
}

class DisciplineService {
  private base = "/discipline";

  // ----- 접근 티어 (프론트 게이팅) -----
  async getAccess(): Promise<DisciplineAccess> {
    const r = await api.get(`${this.base}/access`);
    return r.data;
  }

  // ----- 기준 -----
  async listGroundTypes(includeInactive = false): Promise<DisciplineGroundType[]> {
    const r = await api.get(`${this.base}/ground-types`, { params: { include_inactive: includeInactive } });
    return r.data;
  }
  async createGroundType(data: Partial<DisciplineGroundType>): Promise<DisciplineGroundType> {
    const r = await api.post(`${this.base}/ground-types`, data);
    return r.data;
  }
  async updateGroundType(id: number, data: Partial<DisciplineGroundType>): Promise<DisciplineGroundType> {
    const r = await api.patch(`${this.base}/ground-types/${id}`, data);
    return r.data;
  }
  async listStandards(groundTypeId?: number): Promise<DisciplineStandard[]> {
    const r = await api.get(`${this.base}/standards`, { params: { ground_type_id: groundTypeId } });
    return r.data;
  }
  async createStandard(data: Partial<DisciplineStandard>): Promise<DisciplineStandard> {
    const r = await api.post(`${this.base}/standards`, data);
    return r.data;
  }
  async updateStandard(id: number, data: Partial<DisciplineStandard>): Promise<DisciplineStandard> {
    const r = await api.patch(`${this.base}/standards/${id}`, data);
    return r.data;
  }
  async recommend(groundTypeId: number, intentLevel: IntentLevel): Promise<Recommendation> {
    const r = await api.get(`${this.base}/standards/recommend`, {
      params: { ground_type_id: groundTypeId, intent_level: intentLevel },
    });
    return r.data;
  }
  async getStandardsStatus(): Promise<{ activated: boolean; version: number; activated_at?: string; ground_count: number; standard_count: number; has_draft: boolean; committee_vote_open: boolean }> {
    const r = await api.get(`${this.base}/standards/status`);
    return r.data;
  }
  async setCommitteeVoteMode(open: boolean): Promise<{ committee_vote_open: boolean }> {
    const r = await api.post(`${this.base}/standards/committee-vote-mode`, { open });
    return r.data;
  }
  async initStandards(): Promise<void> {
    await api.post(`${this.base}/standards/init`);
  }
  async activateStandards(): Promise<{ activated: boolean; version: number; activated_at?: string }> {
    const r = await api.post(`${this.base}/standards/activate`);
    return r.data;
  }
  async getPublishedStandards(): Promise<{
    published: boolean; version: number; activated_at?: string;
    grounds: { name: string; category?: string; rule_article?: string; minor?: string; gross?: string; intentional?: string }[];
  }> {
    const r = await api.get(`${this.base}/standards/published`);
    return r.data;
  }

  // ----- 사건 (관리자) -----
  async listCases(params: CaseListParams): Promise<DisciplineCaseListResponse> {
    const r = await api.get(`${this.base}/admin`, { params });
    return r.data;
  }
  async createCase(data: CaseCreatePayload): Promise<DisciplineCaseDetail> {
    const r = await api.post(`${this.base}/admin`, data);
    return r.data;
  }
  async getCase(id: string): Promise<DisciplineCaseDetail> {
    const r = await api.get(`${this.base}/admin/${id}`);
    return r.data;
  }
  async updateCase(id: string, data: Partial<CaseCreatePayload>): Promise<DisciplineCaseDetail> {
    const r = await api.patch(`${this.base}/admin/${id}`, data);
    return r.data;
  }
  async deleteCase(id: string, reason?: string): Promise<void> {
    // 진행된 사건은 사유 필수(감사로그 보존). DELETE 바디로 사유 전달.
    await api.delete(`${this.base}/admin/${id}`, { data: reason ? { reason } : {} });
  }
  async restoreCase(id: string): Promise<void> {
    await api.post(`${this.base}/admin/${id}/restore`);
  }
  async getHistory(id: string): Promise<{ items: any[]; total: number }> {
    const r = await api.get(`${this.base}/admin/${id}/history`);
    return r.data;
  }
  async requestDocuments(id: string, body: {
    requires_explanation?: boolean; requires_apology?: boolean;
    requires_defense?: boolean; submission_due_date?: string;
  }): Promise<DisciplineCaseDetail> {
    const r = await api.post(`${this.base}/admin/${id}/request-documents`, body);
    return r.data;
  }
  async approveDocument(documentId: number): Promise<DisciplineDocument> {
    const r = await api.post(`${this.base}/admin/documents/${documentId}/approve`);
    return r.data;
  }
  async rejectDocument(documentId: number, reason: string): Promise<DisciplineDocument> {
    const r = await api.post(`${this.base}/admin/documents/${documentId}/reject`, { reason });
    return r.data;
  }
  async recordAction(id: string, body: {
    action_type: DisciplineActionType; action_detail?: string;
    action_effective_date?: string; action_deviation_reason?: string;
    expiry_date?: string; retention_until?: string;
  }): Promise<DisciplineCaseDetail> {
    const r = await api.post(`${this.base}/admin/${id}/record-action`, body);
    return r.data;
  }
  async closeCase(id: string): Promise<DisciplineCaseDetail> {
    const r = await api.post(`${this.base}/admin/${id}/close`);
    return r.data;
  }
  async cancelCase(id: string, reason: string): Promise<DisciplineCaseDetail> {
    const r = await api.post(`${this.base}/admin/${id}/cancel`, { reason });
    return r.data;
  }
  async toggleLegalHold(id: string, legalHold: boolean): Promise<DisciplineCaseDetail> {
    const r = await api.post(`${this.base}/admin/${id}/legal-hold`, { legal_hold: legalHold });
    return r.data;
  }
  async addComment(body: {
    case_id: string; content: string; visible_to_employee?: boolean; target_user_id?: string;
  }): Promise<DisciplineComment> {
    const r = await api.post(`${this.base}/admin/comments`, body);
    return r.data;
  }
  async uploadEvidence(caseId: string, file: File, meta: { title: string; description?: string; evidence_type?: string }) {
    const fd = new FormData();
    fd.append("file", file);
    const r = await api.post(`${this.base}/admin/${caseId}/evidence`, fd, {
      params: meta, headers: { "Content-Type": "multipart/form-data" },
    });
    return r.data;
  }
  async deleteEvidence(evidenceId: number): Promise<void> {
    await api.delete(`${this.base}/admin/evidence/${evidenceId}`);
  }

  // ----- 위원회 -----
  async referCommittee(id: string, body: { scheduled_at?: string; location?: string; agenda?: string; committee_type?: string; proposed_action_type?: DisciplineActionType }): Promise<DisciplineCaseDetail> {
    const r = await api.post(`${this.base}/admin/${id}/refer-committee`, body);
    return r.data;
  }
  async proposeCommittee(committeeId: string, proposedActionType: DisciplineActionType): Promise<DisciplineCaseDetail> {
    const r = await api.post(`${this.base}/admin/committees/${committeeId}/propose`, { proposed_action_type: proposedActionType });
    return r.data;
  }
  async openCommitteeVoting(committeeId: string): Promise<DisciplineCaseDetail> {
    const r = await api.post(`${this.base}/admin/committees/${committeeId}/open-voting`);
    return r.data;
  }
  async setMembers(committeeId: string, members: { user_id?: string; external_name?: string; external_email?: string; role: CommitteeMemberRole }[]): Promise<DisciplineCaseDetail> {
    const r = await api.post(`${this.base}/admin/committees/${committeeId}/members`, { members });
    return r.data;
  }
  async removeMember(committeeId: string, memberId: number): Promise<DisciplineCaseDetail> {
    const r = await api.delete(`${this.base}/admin/committees/${committeeId}/members/${memberId}`);
    return r.data;
  }
  async setMemberRole(committeeId: string, memberId: number, role: CommitteeMemberRole): Promise<DisciplineCaseDetail> {
    const r = await api.patch(`${this.base}/admin/committees/${committeeId}/members/${memberId}/role`, { role });
    return r.data;
  }
  async committeeNotify(committeeId: string, body?: { scheduled_at?: string; location?: string }): Promise<DisciplineCaseDetail> {
    const r = await api.post(`${this.base}/admin/committees/${committeeId}/notify`, body || {});
    return r.data;
  }
  async memberOpinion(committeeId: string, memberId: number, body: { opinion?: string; vote?: VoteType }): Promise<DisciplineCaseDetail> {
    const r = await api.post(`${this.base}/admin/committees/${committeeId}/members/${memberId}/opinion`, body);
    return r.data;
  }
  async resolveCommittee(committeeId: string, body: { resolution?: string; minutes?: string }): Promise<DisciplineCaseDetail> {
    const r = await api.post(`${this.base}/admin/committees/${committeeId}/resolve`, body);
    return r.data;
  }

  async getStats(includeExpunged = false): Promise<DisciplineStats> {
    const r = await api.get(`${this.base}/admin/stats`, { params: { include_expunged: includeExpunged } });
    return r.data;
  }

  private triggerDownload(data: Blob, fileName: string) {
    const url = window.URL.createObjectURL(data);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }
  async downloadFile(fileId: number, fileName: string): Promise<void> {
    const r = await api.get(`${this.base}/files/${fileId}/download`, { responseType: "blob" });
    this.triggerDownload(r.data, fileName);
  }
  async downloadEvidence(evidenceId: number, fileName: string): Promise<void> {
    const r = await api.get(`${this.base}/admin/evidence/${evidenceId}/download`, { responseType: "blob" });
    this.triggerDownload(r.data, fileName);
  }

  // ----- 직원 본인 -----
  async myCases(): Promise<DisciplineCaseListItem[]> {
    const r = await api.get(`${this.base}/my`);
    return r.data;
  }
  /** 사이드바 배지 — 본인이 서류(경위서 등)를 제출해야 하는 사건 수 */
  async myPendingCount(): Promise<{ count: number }> {
    const r = await api.get(`${this.base}/my/pending-count`);
    return r.data;
  }
  async myCase(id: string): Promise<MyDisciplineCaseDetail> {
    const r = await api.get(`${this.base}/my/${id}`);
    return r.data;
  }
  async saveDocumentContent(documentId: number, content: string): Promise<DisciplineDocument> {
    const r = await api.put(`${this.base}/my/documents/${documentId}/content`, { content });
    return r.data;
  }
  async uploadDocumentFile(documentId: number, file: File): Promise<DisciplineDocument> {
    const fd = new FormData();
    fd.append("file", file);
    const r = await api.post(`${this.base}/my/documents/${documentId}/files`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return r.data;
  }
  async deleteDocumentFile(fileId: number): Promise<void> {
    await api.delete(`${this.base}/my/documents/files/${fileId}`);
  }
  async submitDocument(documentId: number): Promise<DisciplineDocument> {
    const r = await api.post(`${this.base}/my/documents/${documentId}/submit`);
    return r.data;
  }
  async requestReexamination(caseId: string, reason: string): Promise<MyDisciplineCaseDetail> {
    const r = await api.post(`${this.base}/my/${caseId}/request-reexamination`, { reason });
    return r.data;
  }

  // ----- 위원 (committee member) -----
  async myCommitteeAssignments(): Promise<any[]> {
    const r = await api.get(`${this.base}/committee/my`);
    return r.data;
  }
  async committeeReview(committeeId: string): Promise<any> {
    const r = await api.get(`${this.base}/committee/${committeeId}`);
    return r.data;
  }
  async submitMyOpinion(committeeId: string, body: { opinion?: string; vote?: VoteType }): Promise<any> {
    const r = await api.post(`${this.base}/committee/${committeeId}/my-opinion`, body);
    return r.data;
  }
  // 위원장 진행 권한 위임 — 위원장(내부 사용자)이 관리자 없이 표결 개시·의결
  async chairOpenVoting(committeeId: string): Promise<any> {
    const r = await api.post(`${this.base}/committee/${committeeId}/open-voting`);
    return r.data;
  }
  async chairResolve(committeeId: string, body: { resolution?: string; minutes?: string }): Promise<any> {
    const r = await api.post(`${this.base}/committee/${committeeId}/resolve`, body);
    return r.data;
  }
  /** 오프라인(대면) 의결 결과 등록 — 위원장이 결과·증빙서류(1건 이상 필수)를 직접 등록. resolved_action_type 미지정 = 부결 */
  async chairOfflineResolve(committeeId: string, data: {
    resolution: string; resolved_action_type?: DisciplineActionType; resolved_at?: string; files: File[];
  }): Promise<any> {
    const fd = new FormData();
    fd.append("resolution", data.resolution);
    if (data.resolved_action_type) fd.append("resolved_action_type", data.resolved_action_type);
    if (data.resolved_at) fd.append("resolved_at", data.resolved_at);
    data.files.forEach((f) => fd.append("files", f));
    const r = await api.post(`${this.base}/committee/${committeeId}/offline-resolve`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return r.data;
  }
  /** 오프라인 의결 등록 취소(정정) — 위원장 전용, 처분 기록 전까지. 증빙 삭제 + 등록 전 상태 복귀 */
  async chairVoidOfflineResolution(committeeId: string, reason?: string): Promise<any> {
    const r = await api.post(`${this.base}/committee/${committeeId}/void-offline-resolution`, { reason });
    return r.data;
  }
  /** 오프라인 의결 증빙 서류 다운로드 (위원·관리자) */
  async downloadCommitteeFile(fileId: number, fileName: string): Promise<void> {
    const r = await api.get(`${this.base}/committee/files/${fileId}/download`, { responseType: "blob" });
    this.triggerDownload(r.data, fileName);
  }

  // ----- 외부위원 공개 투표 (token, 인증 불필요) -----
  async getPublicVote(token: string): Promise<any> {
    const r = await api.get(`${this.base}/vote/${token}`);
    return r.data;
  }
  async submitPublicVote(token: string, body: { opinion?: string; vote?: VoteType }): Promise<any> {
    const r = await api.post(`${this.base}/vote/${token}`, body);
    return r.data;
  }
}

export const disciplineService = new DisciplineService();
export default disciplineService;
